'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const SEASONS = ['2024-25', '2023-24', '2022-23', '2021-22', '2020-21'];

const IMPORT_STATS = [
  { label: 'Matchs historiques', value: '4,127', icon: 'CalendarDaysIcon', color: 'text-primary', sub: '5 saisons importées' },
  { label: 'Stats équipes', value: '164', icon: 'ChartBarIcon', color: 'text-positive', sub: '32 équipes × 5 saisons' },
  { label: 'Stats gardiens', value: '892', icon: 'ShieldCheckIcon', color: 'text-accent', sub: 'Tous les gardiens actifs' },
  { label: 'xGoals records', value: '82,540', icon: 'BoltIcon', color: 'text-warning', sub: 'Données shot-level' },
];

const DATA_QUALITY = [
  { season: '2024-25', completeness: 98.7, duplicates: 0.3, normalized: 99.1, validated: 97.8 },
  { season: '2023-24', completeness: 97.2, duplicates: 0.8, normalized: 98.4, validated: 96.5 },
  { season: '2022-23', completeness: 95.8, duplicates: 1.2, normalized: 97.1, validated: 94.9 },
  { season: '2021-22', completeness: 94.1, duplicates: 1.7, normalized: 95.8, validated: 93.2 },
  { season: '2020-21', completeness: 91.3, duplicates: 2.4, normalized: 93.2, validated: 90.1 },
];

const RECENT_IMPORTS = [
  { id: 1, type: 'game_results', season: '2024-25', records: 847, status: 'success', duration: '2.3s', time: '21:45' },
  { id: 2, type: 'goalie_stats', season: '2024-25', records: 124, status: 'success', duration: '0.8s', time: '21:44' },
  { id: 3, type: 'team_stats', season: '2024-25', records: 32, status: 'success', duration: '0.4s', time: '21:43' },
  { id: 4, type: 'xgoals_data', season: '2023-24', records: 16420, status: 'success', duration: '8.7s', time: '21:30' },
  { id: 5, type: 'injury_data', season: '2024-25', records: 287, status: 'warning', duration: '1.2s', time: '21:15' },
  { id: 6, type: 'schedule_data', season: '2025-26', records: 1312, status: 'success', duration: '1.9s', time: '20:58' },
];

const TYPE_LABELS: Record<string, string> = {
  game_results: 'Résultats matchs',
  goalie_stats: 'Stats gardiens',
  team_stats: 'Stats équipes',
  xgoals_data: 'xGoals data',
  injury_data: 'Données blessures',
  schedule_data: 'Calendrier',
};

const TOOLTIP_STYLE = {
  backgroundColor: '#111827',
  border: '1px solid #1E2D4A',
  borderRadius: '6px',
  color: '#E8EDF5',
  fontSize: '11px',
};

export default function HistoricalDataClient() {
  const [importing, setImporting] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('2024-25');
  const [importProgress, setImportProgress] = useState(0);

  const handleImport = () => {
    setImporting(true);
    setImportProgress(0);
    const interval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setImporting(false);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Historical Data Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Import, nettoyage et normalisation des données NHL historiques
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
            className="bg-muted border border-border rounded px-3 py-1.5 text-sm text-foreground"
          >
            {SEASONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={handleImport}
            disabled={importing}
            className="btn-primary flex items-center gap-2"
          >
            <Icon name="ArrowDownTrayIcon" size={14} />
            {importing ? `Import... ${Math.round(importProgress)}%` : 'Import massif'}
          </button>
        </div>
      </div>

      {/* Import progress */}
      {importing && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Import en cours — Saison {selectedSeason}</span>
            <span className="text-sm font-mono-data text-primary">{Math.round(importProgress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${importProgress}%` }}
            />
          </div>
          <div className="flex items-center gap-4 mt-2 text-2xs text-muted-foreground font-mono-data">
            <span>✓ Téléchargement API</span>
            <span className={importProgress > 30 ? 'text-positive' : ''}>✓ Nettoyage données</span>
            <span className={importProgress > 60 ? 'text-positive' : ''}>✓ Normalisation</span>
            <span className={importProgress > 80 ? 'text-positive' : ''}>✓ Validation</span>
            <span className={importProgress > 90 ? 'text-positive' : ''}>✓ Déduplication</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {IMPORT_STATS.map((stat) => (
          <div key={stat.label} className="card stat-card-hover p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={14} className={stat.color} />
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">{stat.label}</span>
            </div>
            <p className={`text-2xl font-bold font-mono-data ${stat.color}`}>{stat.value}</p>
            <p className="text-2xs text-muted-foreground mt-1">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* Data Quality Chart + Recent Imports */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CheckBadgeIcon" size={14} className="text-positive" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Qualité des données par saison</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={DATA_QUALITY} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
              <XAxis dataKey="season" tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[85, 100]} tick={{ fill: '#6B7A99', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`]} />
              <Legend wrapperStyle={{ fontSize: '10px', color: '#6B7A99' }} />
              <Bar dataKey="completeness" name="Complétude" fill="#00D4FF" radius={[2, 2, 0, 0]} />
              <Bar dataKey="normalized" name="Normalisé" fill="#00C853" radius={[2, 2, 0, 0]} />
              <Bar dataKey="validated" name="Validé" fill="#FFB800" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="ClockIcon" size={14} className="text-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Imports récents</span>
          </div>
          <div className="space-y-2">
            {RECENT_IMPORTS.map((imp) => (
              <div key={imp.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${imp.status === 'success' ? 'bg-positive' : 'bg-warning'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground truncate">{TYPE_LABELS[imp.type]}</p>
                  <p className="text-2xs text-muted-foreground font-mono-data">{imp.records.toLocaleString()} records · {imp.duration}</p>
                </div>
                <span className="text-2xs text-muted-foreground font-mono-data">{imp.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Pipeline */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="CpuChipIcon" size={14} className="text-accent" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pipeline de traitement</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { step: '01', label: 'Import API', desc: 'NHL Stats API + ESPN', icon: 'CloudArrowDownIcon', color: 'text-primary' },
            { step: '02', label: 'Nettoyage', desc: 'Suppression valeurs nulles', icon: 'SparklesIcon', color: 'text-positive' },
            { step: '03', label: 'Normalisation', desc: 'Z-score + min-max scaling', icon: 'AdjustmentsHorizontalIcon', color: 'text-accent' },
            { step: '04', label: 'Validation', desc: 'Règles métier NHL', icon: 'CheckBadgeIcon', color: 'text-warning' },
            { step: '05', label: 'Déduplication', desc: 'Hash-based dedup', icon: 'DocumentDuplicateIcon', color: 'text-primary' },
          ].map((step) => (
            <div key={step.step} className="bg-muted/50 rounded-lg p-3 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xs font-mono-data text-muted-foreground">{step.step}</span>
                <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={13} className={step.color} />
              </div>
              <p className="text-xs font-semibold text-foreground">{step.label}</p>
              <p className="text-2xs text-muted-foreground mt-0.5">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tables overview */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="CircleStackIcon" size={14} className="text-primary" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tables de données</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="table-header">Table</th>
                <th className="table-header">Enregistrements</th>
                <th className="table-header">Dernière MAJ</th>
                <th className="table-header">Taille</th>
                <th className="table-header">Statut</th>
              </tr>
            </thead>
            <tbody>
              {[
                { table: 'historical_games', records: '4,127', updated: '21:45 ET', size: '2.3 MB', status: 'ok' },
                { table: 'historical_team_stats', records: '164', updated: '21:44 ET', size: '0.4 MB', status: 'ok' },
                { table: 'historical_goalie_stats', records: '892', updated: '21:44 ET', size: '0.8 MB', status: 'ok' },
                { table: 'model_results', records: '12,847', updated: '21:30 ET', size: '5.1 MB', status: 'ok' },
                { table: 'prediction_accuracy', records: '245', updated: '21:30 ET', size: '0.2 MB', status: 'ok' },
              ].map((row) => (
                <tr key={row.table} className="border-b border-border/50 row-hover">
                  <td className="table-cell font-mono-data text-primary">{row.table}</td>
                  <td className="table-cell font-mono-data">{row.records}</td>
                  <td className="table-cell text-muted-foreground font-mono-data">{row.updated}</td>
                  <td className="table-cell text-muted-foreground font-mono-data">{row.size}</td>
                  <td className="table-cell">
                    <span className="status-badge bg-positive-subtle text-positive border border-positive/20 text-2xs">
                      ✓ {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
